import React, { useState, useRef, useEffect } from 'react'
import { Button, Grid, Line, Picker, Row } from 'components'
import { SPACING } from 'utils'
import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled
} from './ReportCounterpart.style'
import { yup as Yup, yupResolver } from 'utils/yup'
import moment from 'moment'
import { set, useForm } from 'react-hook-form'
import { useAuth, useLoading, useModal } from 'hooks'
import { ReportsCounterpartService } from 'services/NGT/ReportsCounterpart'
import Draggable from 'react-draggable'
import { DraggableDialog } from 'components/DraggableDialog/DraggableDialog'
import { useHistory } from 'react-router-dom'
import zIndex from '@mui/material/styles/zIndex'
import styled from 'styled-components'
import { BackgroundShadow } from '../../../../components/DraggableDialog/styles/BackgroundShadow'

const schema = Yup.object().shape({
  competenciaFim: Yup.string()
    .date()
    .daterange({
      compare: 'competenciaInicio',
      message: 'Data não pode ser menor que a data de início!',
      options: {
        compareEqual: true
      },
      period: 'end'
    })
    .nullable(),
  competenciaInicio: Yup.string()
    .date()
    .daterange({
      compare: 'competenciaFim',
      message: 'Data não pode ser maior que a data de fim!',
      options: {
        compareEqual: true
      },
      period: 'begin'
    })
    .nullable()
})

export const downloadURI = (uri, name) => {
  const link = document.createElement('a')
  link.download = name
  link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${uri}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

interface IReportsCounterpart {
  showModal2: boolean
}

export const ReportsCounterpart: React.FC<IReportsCounterpart> = ({
  showModal2
}) => {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({ resolver: yupResolver(schema) })
  const { user } = useAuth()
  const { goBack } = useHistory()
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [showLoading, setShowLoading] = useState(false)

  // Faz as validações dos paramêtros e gera o EXCEL
  const generateExcelRelatorioContraparte = async () => {
    setShowLoading(true)
    setLoading('loading')

    if (user.PerfilUsuarioId == 6) {
      ReportsCounterpartService()
        .GetExcelRelatorioContraparte(dataInicio, dataFim, user.IdUsuario)
        .then((response) => {
          if (response.status === 200) {
            if (response.data != '')
              downloadURI(response.data, `relatorio-contraparte.xlsx`)
            else
              showModal({
                hideButtonCancel: true,
                message:
                  'Não foram encontrado dados para o período de competência selecionado!',
                txtButtonConfirm: 'Fechar',
                type: 'alert'
              })
          }
          setLoading('idle')
          setShowLoading(false)
        })
        .catch((error) => {
          setLoading('idle')
          setShowLoading(false)
          showModal({
            hideButtonCancel: true,
            message:
              // error.mensagem ||
              // error.message ||
              'Não foi possivel fazer o download da planilha Relatório Contraparte',
            txtButtonConfirm: 'Fechar',
            type: 'error'
          })
        })
    }
    if (
      user.PerfilUsuarioId == 2 ||
      user.PerfilUsuarioId == 3 ||
      user.PerfilUsuarioId == 1
    ) {
      ReportsCounterpartService()
        .GetAllRelatorioContraparte(dataInicio, dataFim)
        .then((response) => {
          if (response.status === 200) {
            if (response.data != '')
              downloadURI(response.data, `relatorio-contraparte.xlsx`)
            else
              showModal({
                hideButtonCancel: true,
                message:
                  'Não foram encontrado dados para o período de competência selecionado!',
                txtButtonConfirm: 'Fechar',
                type: 'alert'
              })
          }
          setShowLoading(false)
          setLoading('idle')
        })
        .catch((error) => {
          setShowLoading(false)
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message:
              // error.mensagem ||
              // error.message ||
              'Não foi possivel fazer o download da planilha Relatório Contraparte',
            txtButtonConfirm: 'Fechar',
            type: 'error'
          })
        })
    }
  }

  // Formata a data
  const competenciaInicio = watch('competenciaInicio')
  const dataInicio = moment(new Date(competenciaInicio)).format('YYYYMM')

  const competenciaFim = watch('competenciaFim')
  const dataFim = moment(new Date(competenciaFim)).format('YYYYMM')

  // Mantem os dateForms com as datas com  1 mês de diferença
  const DATE_NOW = moment()
    .set({ date: 1, hour: 0, millisecond: 0, minute: 0, second: 0 })
    .toISOString()
  const COMPETENCIA_ATUAL = moment(DATE_NOW).subtract(1, 'month').toISOString()

  useEffect(() => {
    setValue('competenciaFim', DATE_NOW)
    setValue('competenciaInicio', COMPETENCIA_ATUAL)
  }, [showModal])

  useEffect(() => {
    const subs = watch(() => trigger())
    return () => subs.unsubscribe()
  }, [watch])

  const draggableReference = useRef<Draggable>(null)

  // HTML usado na tela
  return (
    <>
      <DraggableDialog
        reference={draggableReference}
        showModal={showModal2}
        width={'sm'}
        style={{ zIndex: `${showLoading ? 1302 : 1300}` }}
      >
        <Line />
        <DialogTitleStyled>Relatório Contraparte</DialogTitleStyled>
        <DialogContentStyled>
          <Grid
            gridCols="1fr 1fr"
            gridGap={`0 ${SPACING.spacing30}`}
            margin={`${SPACING.spacing20} 0 ${SPACING.spacing20} 0`}
          >
            <Picker
              defaultValue={competenciaInicio}
              label="Competência Inicial"
              views={['year', 'month']}
              placeholder="mm/aaaa"
              formValue="competenciaInicio"
              maxDate={moment(competenciaFim)}
              type="monthAndYear"
              control={control}
              errors={errors}
            />
            <Picker
              defaultValue={competenciaFim}
              label="Competência Final"
              views={['year', 'month']}
              placeholder="mm/aaaa"
              formValue="competenciaFim"
              minDate={moment(competenciaInicio)}
              type="monthAndYear"
              control={control}
              errors={errors}
            />
          </Grid>
        </DialogContentStyled>
        <DialogActionsStyled>
          <Button
            text="Voltar"
            variantButton="outlined"
            onClick={() => goBack()}
            marginBottom={SPACING.spacing20}
          />
          <Row justifyContent="flex-end">
            <Button
              text="Visualizar Planilha"
              type="submit"
              marginLeft={SPACING.spacing20}
              marginRight={SPACING.spacing20}
              marginBottom={SPACING.spacing20}
              onClick={generateExcelRelatorioContraparte}
            />
          </Row>
        </DialogActionsStyled>
      </DraggableDialog>
    </>
  )
}
